import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FileSaverService } from 'ngx-filesaver';
import { Observable, takeWhile, tap } from 'rxjs';
import { BaseStyleClassesDirective } from '../../gk-directives';
import { ApiNewDokumentPowiazanyDalDto } from '../../gk-dynamic-list/services/add-related-document/add-related-document.model';
import { FieldId } from '../../gk-dynamic-list/services/dictionary/dictionary.model';
import { StampDutyService } from '../../services';
import { docFileToDtoDocument } from '../../utils/files/files.util';
import { PortalId } from '../../utils/portal-id/portal-id.model';
import { getFileNameFromResponseHeaders } from '../../utils/utils';
import {
  ChosenDocumentSections,
  DocumentSection,
  DocumentSectionTypeId,
} from './services/document-section/document-section.model';
import { DocumentSectionService } from './services/document-section/document-section.service';

@Component({
  selector: 'gk-attachments',
  templateUrl: './attachments.component.html',
  standalone: false,
})
export class AttachmentsComponent
  extends BaseStyleClassesDirective
  implements OnDestroy, OnInit, OnChanges
{
  private isAlive = true;
  documentSections: DocumentSection[];
  chosenFiles: ChosenDocumentSections = {};
  documentsPaidInCurrentSectionIds = new Set<string | number>();
  @Input() submitted: boolean;
  @Input() portalId: PortalId;
  @Input() requiredPowerOfAttorney: boolean = undefined;
  @Input() requiredOtherFile: boolean = undefined;
  @Input() requiredRequestMode = false;
  @Input() requestMode: FieldId;
  @Input() attachmentsInfo: string;

  constructor(
    public documentSectionService: DocumentSectionService,
    private fileSaverService: FileSaverService,
    public stampDutyService: StampDutyService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.fetchDocumentSections();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['requiredPowerOfAttorney']) {
      this.updateIsObligatoryProperty(
        DocumentSectionTypeId.PowerOfAttorney,
        this.requiredPowerOfAttorney,
      );
    }
    if (changes['requiredOtherFile']) {
      this.updateIsObligatoryProperty(
        DocumentSectionTypeId.OtherFile,
        this.requiredOtherFile,
      );
    }
    if (changes['requestMode']) {
      this.fetchDocumentSections();
    }
  }

  updateIsObligatoryProperty(
    typeId: DocumentSectionTypeId,
    expectedSate: boolean,
  ): void {
    this.documentSections = this.documentSections?.map((documentSection) => {
      if (
        documentSection.id === typeId &&
        documentSection.isObligatory !== expectedSate
      ) {
        documentSection.isObligatory = expectedSate;
      }

      return documentSection;
    });
  }

  fetchDocumentSections(): void {
    if (this.requiredRequestMode && !this.requestMode) {
      return;
    }
    this.chosenFiles = {};
    this.documentSectionService
      .get(this.portalId, this.requestMode)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((data) => {
        this.documentSections = data;
        this.updateIsObligatoryProperty(
          DocumentSectionTypeId.PowerOfAttorney,
          this.requiredPowerOfAttorney,
        );
        this.updateIsObligatoryProperty(
          DocumentSectionTypeId.OtherFile,
          this.requiredOtherFile,
        );
      });
  }

  shouldShowFileInputInvalidMessage(documentSection: DocumentSection): boolean {
    return (
      this.submitted &&
      !this.areChosenObligatoryDocuments(documentSection) &&
      !this.isObligatoryStampDutyPaid(documentSection)
    );
  }

  handleFileInputAction(id: string | number, files: File[]): void {
    this.chosenFiles[id] = files;
    this.notifyProxyAttachmentSelection();
  }

  notifyProxyAttachmentSelection(): void {
    const isProxyAttachmentChosen =
      !!this.chosenFiles[DocumentSectionTypeId.PowerOfAttorney]?.length ||
      !!this.chosenFiles[DocumentSectionTypeId.PowerOfAttorneyStampDuty]
        ?.length;
    const selected$ = this.documentSectionService.getProxyAttachmentsSelected(
      this.portalId,
    );
    selected$.next(isProxyAttachmentChosen);
  }

  downloadSampleDocument(sampleDocumentId: string | number): void {
    this.documentSectionService
      .downloadSampleDocument(sampleDocumentId)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((data) => {
        const fileName = getFileNameFromResponseHeaders(data.headers);
        this.fileSaverService.save(data.body, fileName);
      });
  }

  areChosenDocuments(): boolean {
    return this.documentSections?.some(
      (documentSection: DocumentSection) =>
        !!(
          this.chosenFiles[documentSection.id] &&
          this.chosenFiles[documentSection.id].length
        ),
    );
  }

  isChosenDocument(documentSectionId: string | number): boolean {
    return !!(
      this.chosenFiles[documentSectionId] &&
      this.chosenFiles[documentSectionId].length
    );
  }

  getConvertedFiles(): Promise<ApiNewDokumentPowiazanyDalDto | Error>[] {
    return Object.entries(this.chosenFiles).flatMap(([id, files]) =>
      files.map((file) => docFileToDtoDocument(file, id)),
    );
  }

  areDocumentsValid(): boolean {
    return this.documentSections.every(
      (documentSection) =>
        this.areChosenObligatoryDocuments(documentSection) ||
        this.isObligatoryStampDutyPaid(documentSection),
    );
  }

  areChosenObligatoryDocuments(documentSection: DocumentSection): boolean {
    const files = documentSection.alternativeDocumentTypes?.length
      ? documentSection.alternativeDocumentTypes
          .map(
            (alternativeDocumentType) =>
              !!this.chosenFiles[alternativeDocumentType.id]?.length,
          )
          .filter(Boolean)
      : this.chosenFiles[documentSection.id];

    return !documentSection.isObligatory || !!(files && files.length);
  }

  isObligatoryStampDutyPaid(documentSection: DocumentSection): boolean {
    return (
      !documentSection.isObligatory ||
      documentSection.stampDuty?.isInitialPaid ||
      this.documentsPaidInCurrentSectionIds.has(documentSection.id) ||
      !!documentSection.alternativeDocumentTypes?.some(
        (alternativeDocumentType) =>
          this.documentsPaidInCurrentSectionIds.has(
            alternativeDocumentType.id,
          ) || alternativeDocumentType.stampDuty?.isInitialPaid,
      )
    );
  }

  isStampDutyPaidInCurrentSession(
    documentSectionId: string | number,
  ): Observable<boolean> {
    return this.stampDutyService
      .isPaidInCurrentSession(this.portalId, documentSectionId)
      .pipe(
        tap((isPaid) => {
          if (isPaid) {
            this.documentsPaidInCurrentSectionIds.add(documentSectionId);
          }
        }),
      );
  }

  triggerStampDutyPaymentStatusCheck(documentSecionId: string | number): void {
    this.stampDutyService.deletePaymentStatusCache(
      this.portalId,
      documentSecionId,
    );
  }

  ngOnDestroy(): void {
    this.deleteDocumentSectionCache();
    this.deletePaymentStatusCacheForAllDocumentSections();
    this.isAlive = false;
  }

  deleteDocumentSectionCache(): void {
    this.documentSectionService.clearCache(this.portalId, this.requestMode);
  }

  deletePaymentStatusCacheForAllDocumentSections(): void {
    this.documentSections?.forEach((documentSection) =>
      this.triggerStampDutyPaymentStatusCheck(documentSection.id),
    );
  }
}
